import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import DeviceMap from '../components/DeviceMap';
import { toast } from 'sonner';
import { 
  Map, 
  RefreshCw, 
  List,
  Wifi,
  WifiOff,
  ArrowLeft,
  Cloud
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MapPage = () => {
  const { t } = useLanguage();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${API}/devices`);
      setDevices(response.data);
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const syncFromCloud = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/fac/devices/sync`);
      toast.success(`${response.data.message}`);
      fetchDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from FAC Cloud');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  // Count online/offline devices
  const onlineCount = devices.filter(d => d.status === 'online').length;
  const offlineCount = devices.filter(d => d.status !== 'online').length;
  const openCount = devices.filter(d => d.valve_state === 'open').length;

  return (
    <div className="min-h-screen bg-fac-dark pt-16 pb-24 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/app">
              <Button variant="ghost" className="text-white hover:bg-fac-card">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-white uppercase flex items-center gap-3">
                <Map className="w-8 h-8 text-fac-green" />
                Device Map
                <span className="text-fac-green font-chinese text-xl">設備地圖</span>
              </h1>
              <p className="text-muted-foreground mt-1 font-chinese">
                View all your smart valves on the map / 在地圖上查看所有智能閥門
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={syncFromCloud}
              disabled={syncing}
              variant="outline"
              className="border-fac-blue text-fac-blue hover:bg-fac-blue/10"
              data-testid="sync-cloud-btn"
            >
              {syncing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Cloud className="w-4 h-4 mr-2" />
              )}
              Sync Cloud
            </Button>
            <Link to="/app">
              <Button variant="outline" className="border-fac-green text-fac-green hover:bg-fac-green/10">
                <List className="w-4 h-4 mr-2" />
                List View
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-fac-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-white">{devices.length}</p>
              <p className="text-sm text-muted-foreground font-chinese">Total Devices / 設備總數</p>
            </CardContent>
          </Card>
          <Card className="bg-fac-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-fac-green flex items-center justify-center gap-2">
                <Wifi className="w-6 h-6" />
                {onlineCount}
              </p>
              <p className="text-sm text-muted-foreground font-chinese">Online / 在線</p>
            </CardContent>
          </Card>
          <Card className="bg-fac-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-fac-red flex items-center justify-center gap-2">
                <WifiOff className="w-6 h-6" />
                {offlineCount}
              </p>
              <p className="text-sm text-muted-foreground font-chinese">Offline / 離線</p>
            </CardContent>
          </Card>
          <Card className="bg-fac-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-fac-blue">{openCount}</p>
              <p className="text-sm text-muted-foreground font-chinese">Valves Open / 閥門開啟</p>
            </CardContent>
          </Card>
        </div>

        {/* Map */}
        <Card className="bg-fac-card border-border">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <Map className="w-5 h-5 text-fac-green" />
              Device Locations
              <span className="text-fac-green font-chinese text-base ml-2">設備位置</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[500px] flex items-center justify-center">
                <RefreshCw className="w-8 h-8 text-fac-green animate-spin" />
              </div>
            ) : devices.length === 0 ? (
              <div className="h-[500px] flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <Map className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="font-chinese">No devices found / 未找到設備</p>
                  <Button onClick={syncFromCloud} className="mt-4 bg-fac-green text-black">
                    Sync from FAC Cloud
                  </Button>
                </div>
              </div>
            ) : (
              <DeviceMap devices={devices} height="500px" />
            )}
          </CardContent>
        </Card>

        {/* Device List Summary */}
        <Card className="bg-fac-card border-border mt-6">
          <CardHeader>
            <CardTitle className="font-heading text-white uppercase flex items-center gap-2">
              <List className="w-5 h-5 text-fac-blue" />
              Device Summary
              <span className="text-fac-green font-chinese text-base ml-2">設備概覽</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-muted-foreground font-chinese">Device / 設備</th>
                    <th className="text-center py-3 px-4 text-muted-foreground font-chinese">Status / 狀態</th>
                    <th className="text-center py-3 px-4 text-muted-foreground font-chinese">Valve / 閥門</th>
                    <th className="text-center py-3 px-4 text-muted-foreground font-chinese">Pressure / 壓力</th>
                    <th className="text-center py-3 px-4 text-muted-foreground font-chinese">Battery / 電池</th>
                    <th className="text-right py-3 px-4 text-muted-foreground font-chinese">Location / 位置</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((device) => (
                    <tr key={device.id} className="border-b border-border/50 hover:bg-fac-paper/50">
                      <td className="py-3 px-4">
                        <Link to={`/app/device/${device.id}`} className="text-white hover:text-fac-green font-medium">
                          {device.name}
                        </Link>
                        {device.serial_number && (
                          <p className="text-xs text-muted-foreground font-mono">{device.serial_number}</p>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                          device.status === 'online' 
                            ? 'bg-fac-green/20 text-fac-green' 
                            : 'bg-fac-red/20 text-fac-red'
                        }`}>
                          {device.status === 'online' ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                          {device.status === 'online' ? '在線' : '離線'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={device.valve_state === 'open' ? 'text-fac-green' : 'text-white'}>
                          {device.valve_state === 'open' ? '開啟' : '關閉'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-fac-blue">
                        {device.pressure} bar
                      </td>
                      <td className="py-3 px-4 text-center">
                        {device.battery !== undefined && device.battery !== null ? (
                          <span className={device.battery < 20 ? 'text-fac-red' : device.battery < 50 ? 'text-fac-yellow' : 'text-fac-green'}>
                            {device.battery}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right text-muted-foreground text-xs">
                        {device.gps_lat && device.gps_lon ? (
                          <span>{device.gps_lat.toFixed(4)}, {device.gps_lon.toFixed(4)}</span>
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MapPage;
